/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
/**
 * Appetite Settings › Risk Evaluation Criteria › Plausibility Table (ID172)
 * Two modes:
 *  - timeframe_velocity: 3 columns (Timeframe, Velocity, Description). The combined "rating"
 *    (e.g. "Near Term & Fast") is derived and drives the matrix Y-axis + the radar's time rings.
 *  - likelihood: a list of likelihood bands (band name + description), ordered furthest-out →
 *    nearest-centre, which become the radar's likelihood rings (velocity symbol omitted).
 */
import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import MDButton from "components/MDButton";

const TIMEFRAME_OPTIONS = ["Near Term", "Medium Term", "Long Term"];
const VELOCITY_OPTIONS = ["Fast", "Slow"];
const DEFAULT_LIKELIHOOD_BANDS = [
  "Highly Unlikely",
  "Unlikely",
  "Probable",
  "Likely",
  "Highly Likely",
];

function newId() {
  return Math.random().toString(36).slice(2, 10);
}

const tvRating = (tf, v) => [tf, v].filter(Boolean).join(" & ");

const HEAD_SX = { bgcolor: "#ede7f6", color: "#52009b", fontWeight: 700, fontSize: 13 };

export default function PlausibilityTablePanel({ rows, setRows, mode, setMode }) {
  const isLikelihood = mode === "likelihood";

  const update = (id, field, value) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  // Timeframe/Velocity edits also keep the derived "rating" (matrix Y-axis label) in sync.
  const updateTV = (id, field, value) =>
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, [field]: value };
        next.rating = tvRating(
          field === "timeframe" ? value : next.timeframe,
          field === "velocity" ? value : next.velocity
        );
        return next;
      })
    );

  const addRow = () =>
    setRows((prev) => [
      ...prev,
      isLikelihood
        ? { id: newId(), order: prev.length + 1, rating: "", description: "" }
        : {
            id: newId(),
            order: prev.length + 1,
            timeframe: "Near Term",
            velocity: "Fast",
            rating: tvRating("Near Term", "Fast"),
            description: "",
          },
    ]);

  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));

  const loadDefaultBands = () =>
    setRows(
      DEFAULT_LIKELIHOOD_BANDS.map((band, i) => ({
        id: newId(),
        order: i + 1,
        rating: band,
        description: "",
      }))
    );

  return (
    <Box>
      {/* Mode toggle */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, v) => v && setMode(v)}
          sx={{
            "& .MuiToggleButton-root.Mui-selected": {
              bgcolor: "#52009b",
              color: "#fff",
              "&:hover": { bgcolor: "#3a0070" },
            },
          }}
        >
          <ToggleButton value="timeframe_velocity">Timeframe &amp; Velocity</ToggleButton>
          <ToggleButton value="likelihood">Likelihood</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 520 }}>
          {isLikelihood
            ? "Likelihood bands order from furthest-out (top) to nearest-centre (bottom) on the radar; the velocity symbol is hidden."
            : "Timeframe drives the radar ring (Near/Medium/Long); Velocity drives the motion symbol."}
        </Typography>
        {isLikelihood && (
          <MDButton variant="text" color="info" size="small" onClick={loadDefaultBands}>
            Load default bands
          </MDButton>
        )}
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <Table
          sx={{
            tableLayout: "fixed",
            width: "100%",
            borderCollapse: "collapse",
            "& td, & th": {
              border: "1px solid #bdbdbd",
              verticalAlign: "top",
              padding: "10px 14px",
            },
          }}
        >
          <colgroup>
            {isLikelihood ? (
              <>
                <col style={{ width: 260 }} />
                <col />
                <col style={{ width: 48 }} />
              </>
            ) : (
              <>
                <col style={{ width: 170 }} />
                <col style={{ width: 130 }} />
                <col />
                <col style={{ width: 48 }} />
              </>
            )}
          </colgroup>
          <TableHead>
            <TableRow>
              {isLikelihood ? (
                <TableCell sx={HEAD_SX}>Likelihood band</TableCell>
              ) : (
                <>
                  <TableCell sx={HEAD_SX}>Timeframe</TableCell>
                  <TableCell sx={HEAD_SX}>Velocity</TableCell>
                </>
              )}
              <TableCell sx={HEAD_SX}>Description</TableCell>
              <TableCell sx={{ bgcolor: "#ede7f6" }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} sx={{ "&:nth-of-type(even)": { bgcolor: "#fafafa" } }}>
                {isLikelihood ? (
                  <TableCell sx={{ bgcolor: "#f5f5f5" }}>
                    <TextField
                      value={row.rating}
                      onChange={(e) => update(row.id, "rating", e.target.value)}
                      variant="standard"
                      fullWidth
                      placeholder="e.g. Highly Unlikely"
                      inputProps={{ style: { fontWeight: 700, fontSize: 13, color: "#37474f" } }}
                      sx={{ "& .MuiInput-underline:before": { borderBottomColor: "transparent" } }}
                    />
                  </TableCell>
                ) : (
                  <>
                    <TableCell sx={{ bgcolor: "#f5f5f5" }}>
                      <Select
                        value={row.timeframe || ""}
                        onChange={(e) => updateTV(row.id, "timeframe", e.target.value)}
                        variant="standard"
                        fullWidth
                        displayEmpty
                        sx={{ fontSize: 13, fontWeight: 700, color: "#37474f" }}
                      >
                        {TIMEFRAME_OPTIONS.map((o) => (
                          <MenuItem key={o} value={o}>
                            {o}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#f5f5f5" }}>
                      <Select
                        value={row.velocity || ""}
                        onChange={(e) => updateTV(row.id, "velocity", e.target.value)}
                        variant="standard"
                        fullWidth
                        displayEmpty
                        sx={{ fontSize: 13, fontWeight: 700, color: "#37474f" }}
                      >
                        {VELOCITY_OPTIONS.map((o) => (
                          <MenuItem key={o} value={o}>
                            {o}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </>
                )}
                <TableCell>
                  <TextField
                    value={row.description}
                    onChange={(e) => update(row.id, "description", e.target.value)}
                    variant="standard"
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Enter description…"
                    inputProps={{ style: { fontSize: 13 } }}
                    sx={{
                      "& .MuiInput-underline:before": { borderBottomColor: "transparent" },
                      "& .MuiInput-underline:hover:before": { borderBottomColor: "#ccc" },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{ bgcolor: "#eceff1", textAlign: "center", verticalAlign: "middle" }}
                >
                  <Tooltip title="Remove row">
                    <IconButton
                      size="small"
                      onClick={() => removeRow(row.id)}
                      sx={{ color: "#b71c1c" }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={isLikelihood ? 3 : 4} sx={{ bgcolor: "#fafafa", py: 0.5 }}>
                <MDButton
                  variant="text"
                  color="info"
                  startIcon={<AddIcon />}
                  onClick={addRow}
                  size="small"
                >
                  {isLikelihood ? "Add band" : "Add row"}
                </MDButton>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
